/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IMfaSelectionState {
  showMfaSelection: boolean;
  mfaConfigs: Array<{
    userId: string;
    mfaType: 'EMAIL' | 'TOTP';
    createdAt: string;
    backupCount: number;
    isEnabled: boolean;
    verifiedAt: string;
  }>;
  selectedMfaType?: 'EMAIL' | 'TOTP';
  mfaConfigStatus: 'REQUEST_CONFIG_EMAIL' | 'CAN_CONTINUE'
  originalCredentials?: {
    name: string;
    password: string;
    retainLogin: boolean;
  };
  challengeId?: string;
}

export interface IMfaVerificationForm {
  mfaType: 'EMAIL' | 'TOTP';
  code: string;
  isUsingBackupCode?: boolean;
  challengeId?: string;
}

export interface IMfaChallengeAction {
  payload: { mfaType: 'EMAIL' | 'TOTP' };
  type: string;
}

export interface MfaState {
  mfaState: IMfaSelectionState;
  loading: boolean;
  error: string | null;
}

export const initialState: MfaState = {
  mfaState: {
    showMfaSelection: false,
    mfaConfigs: [],
    selectedMfaType: undefined,
    originalCredentials: undefined,
    challengeId: undefined,
    mfaConfigStatus: 'CAN_CONTINUE',
  },
  loading: false,
  error: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mfaSlice = createSlice({
  name: 'feature/base/mfa',
  initialState,
  reducers: {
    setMfaChallenge(
      state: MfaState,
      action: PayloadAction<{ mfaConfigs: any[]; originalCredentials?: any }>,
    ) {
      state.mfaState.showMfaSelection = true;
      state.mfaState.mfaConfigs = action.payload.mfaConfigs;
      state.mfaState.originalCredentials = action.payload.originalCredentials;
      state.loading = false;
    },
    selectMfaType(state: MfaState, action: PayloadAction<{ mfaType: 'EMAIL' | 'TOTP' }>) {
      state.mfaState.selectedMfaType = action.payload.mfaType;
    },
    setMfaConfigStatus(state: MfaState, action: PayloadAction<{
      mfaConfigStatus: 'REQUEST_CONFIG_EMAIL' | 'CAN_CONTINUE',
      originalCredentials?: any
    }>) {
      state.mfaState.mfaConfigStatus = action.payload.mfaConfigStatus;
      state.mfaState.originalCredentials = action.payload.originalCredentials;
    },
    verifyMfa(state: MfaState, _action: PayloadAction<IMfaVerificationForm>) {
      state.loading = true;
    },
    verifyMfaSucceeded(state: MfaState, _action: PayloadAction<any>) {
      state.loading = false;
      state.error = null;
      state.mfaState = initialState.mfaState;
    },
    verifyMfaFailed(state: MfaState, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    resetMfaState(state: MfaState) {
      state.mfaState = initialState.mfaState;
    },
    clearStaleAuthState(state: MfaState) {
      if (state.mfaState.showMfaSelection && !state.loading) {
        state.mfaState = initialState.mfaState;
      }
    },
    initiateMfaChallenge(state: MfaState, _action: PayloadAction<{ mfaType: 'EMAIL' | 'TOTP' }>) {
      state.loading = true;
    },
    initiateMfaChallengeSucceeded(state: MfaState, action: PayloadAction<{ challengeId: string }>) {
      state.mfaState.challengeId = action.payload.challengeId;
      state.loading = false;
    },
    initiateMfaChallengeFailed(state: MfaState, _action: PayloadAction<string>) {
      state.loading = false;
    },
    clearMfaError(state: MfaState) {
      state.error = null;
    },
    resetMfaChallengeState(state: MfaState) {
      state.mfaState.challengeId = undefined;
      state.loading = false;
      state.error = null;
    },
  },
});

export const { actions: mfaSliceActions } = mfaSlice;
export default mfaSlice.reducer;
